import { computed, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationExtras, Params, Router } from '@angular/router';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { filter } from 'rxjs';

// State interface
interface RouterState {
  params: Params;
  queryParams: Params;
  url: string;
  previousUrl?: string;
  activeOutlets: string[];
  data: Record<string, unknown> | null;
}

// Initial state
const initialState: RouterState = {
  params: {},
  queryParams: {},
  url: '',
  previousUrl: null,
  activeOutlets: ['primary'],
  data: null,
};

export const RouterStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    selectUrlCommand: computed(() => {
      let url = store.url() || '';
      const hasQueryParams = url.includes('?');
      if (hasQueryParams) {
        url = url.substring(0, url.indexOf('?'));
      }
      return url.split('/');
    }),
    selectRouteInfo: computed(() => {
      const data = store.data();
      if (data?.breadCrumb) {
        return {
          ...data,
          link: typeof data.link === 'string' ? data.link.replace(/^\//, '') : data.link,
          breadCrumb: `${data.breadCrumb} ${data.cargoNo ? `#${data.cargoNo}` : ''}`,
        };
      }
      return data;
    }),
  })),
  withMethods((store, router = inject(Router)) => {
    // Helper function to extract params with auxiliary routes
    const getInitialParamsWithAux = (state: ActivatedRouteSnapshot): Params => {
      let params = state.params;
      if (state.firstChild) {
        state.children
          .filter((child) => child.outlet !== 'primary')
          .filter((child) => !!child.firstChild)
          .forEach((child) => {
            child.firstChild?.children.forEach((inner) => {
              params = { ...params, ...inner.params };
            });
          });
      }
      return params;
    };

    // Initialize router event listening
    const initializeRouterListener = () => {
      router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
        const url = e.urlAfterRedirects;
        let state = router.routerState.snapshot.root;
        let data = state.data;
        const activeOutlets = state.children.map((r) => r.outlet);
        let params = getInitialParamsWithAux(state);
        const queryParams = { ...state.queryParams };

        while (state.firstChild) {
          state = state.firstChild;
          data = { ...data, ...state.data };
          params = { ...params, ...state.params };
        }

        const payload = { params, queryParams, url, activeOutlets, data };
        navigationComplete(payload);
      });
    };

    // Method to handle navigation completion
    const navigationComplete = (payload: Omit<RouterState, 'previousUrl'>) => {
      patchState(store, {
        ...payload,
        previousUrl: store.url(),
      });
    };

    // Initialize router listener on store creation
    initializeRouterListener();

    return {
      // Navigation method
      navigate: ({ commands, extras }: { commands: string[]; extras?: NavigationExtras }) => {
        const navigationExtras = extras || { queryParamsHandling: 'preserve' };
        const prefix = router.routerState.snapshot.url.split('?')[0].replace(/^\//, '').split('/')[0];

        let finalCommands: string[];
        if (commands.includes(prefix)) {
          finalCommands = commands;
        } else {
          const filteredCommands = [...commands].filter((c) => c !== '/');
          finalCommands = ['/', prefix, ...filteredCommands];
        }

        router.navigate(finalCommands, navigationExtras);
      },

      // Method to get a specific parameter
      selectParam: (name: string) => computed(() => store.params()[name]),

      // Method to set route data
      setRouteData: (routeData: { title: string; value: string }) => {
        patchState(store, (state) => ({
          data: { ...state.data, [routeData.title]: routeData.value },
        }));
      },

      // Method to manually update navigation state (for external use)
      navigationComplete,
    };
  }),
);
